import { parseMask, type TerrainMask } from './mask';

export interface ManifestEntry {
  file: string;
  size: number;
  sha256: string;
  type: string;
}

export interface Manifest {
  manifestVersion: number;
  buildId: string;
  objectsBase: string;
  assets: Record<string, ManifestEntry>;
}

/**
 * URL para baixar o arquivo por `fetch` (modo CORS). O mesmo arquivo também é pedido sem CORS por `<img>`, `background-image` e `<audio>`, e o R2 só manda
 * `Vary: Origin` quando a requisição traz `Origin`. A resposta sem CORS, guardada no cache do navegador (imutável por 1 ano), seria reaproveitada pelo
 * `fetch` seguinte e rejeitada como "erro de CORS". O parâmetro dá a cada tipo de pedido a sua própria entrada de cache; o servidor o ignora.
 */
export function corsUrl(url: string): string {
  return /^(blob|data):/.test(url) ? url : `${url}${url.includes('?') ? '&' : '?'}cors=1`;
}

export class AssetNotFoundError extends Error {
  constructor(public readonly path: string) {
    super(`Asset não está no manifest: ${path}`);
    this.name = 'AssetNotFoundError';
  }
}

/**
 * Catálogo dos assets publicados pelo pipeline (`manifest.json` + `objects/<sha256>.<ext>`, ADR 0009). Resolve o caminho lógico
 * (`atlas/maps-art/1001/fore.webp`) para a URL com hash, que é imutável e pode ficar em cache para sempre.
 */
export class AssetCatalog {
  private readonly maskCache = new Map<string, Promise<TerrainMask>>();

  constructor(
    readonly baseUrl: string,
    readonly manifest: Manifest,
    private readonly fetchImpl: typeof fetch = (...args) => fetch(...args),
  ) {}

  static async load(baseUrl: string, fetchImpl: typeof fetch = (...args) => fetch(...args)): Promise<AssetCatalog> {
    const root = baseUrl.replace(/\/+$/, '');
    // O manifest é o único objeto sem hash no nome: sempre pergunta ao servidor se mudou.
    const response = await fetchImpl(`${root}/manifest.json`, { cache: 'no-cache' });
    if (!response.ok) throw new Error(`Não foi possível carregar o manifest de assets (${response.status}).`);
    const manifest = (await response.json()) as Manifest;
    if (manifest.manifestVersion !== 1 || typeof manifest.assets !== 'object') throw new Error('Manifest de assets em versão não suportada.');
    return new AssetCatalog(root, manifest, fetchImpl);
  }

  has(path: string): boolean {
    return path in this.manifest.assets;
  }

  url(path: string): string {
    const entry = this.manifest.assets[path];
    if (!entry) throw new AssetNotFoundError(path);
    const base = this.manifest.objectsBase.replace(/^\/+|\/+$/g, '');
    return `${this.baseUrl}/${base ? `${base}/` : ''}${entry.file}`;
  }

  async bytes(path: string): Promise<Uint8Array> {
    const response = await this.fetchImpl(corsUrl(this.url(path)));
    if (!response.ok) throw new Error(`Falha ao baixar ${path} (${response.status}).`);
    return new Uint8Array(await response.arrayBuffer());
  }

  /** Máscara (mapa ou cratera), baixada uma vez por sessão. */
  mask(path: string): Promise<TerrainMask> {
    let cached = this.maskCache.get(path);
    if (!cached) {
      cached = this.bytes(path).then(parseMask);
      this.maskCache.set(path, cached);
      cached.catch(() => this.maskCache.delete(path));
    }
    return cached;
  }

  /** Máscara em uma URL qualquer (por exemplo, as de cratera que o site serve junto com o próprio código), baixada uma vez por sessão. */
  maskAt(url: string): Promise<TerrainMask> {
    let cached = this.maskCache.get(url);
    if (!cached) {
      cached = this.fetchImpl(url).then(async (response) => {
        if (!response.ok) throw new Error(`Falha ao baixar ${url} (${response.status}).`);
        return parseMask(new Uint8Array(await response.arrayBuffer()));
      });
      this.maskCache.set(url, cached);
      cached.catch(() => this.maskCache.delete(url));
    }
    return cached;
  }

  mapArt(mapId: number): { back: string | null; fore: string | null } {
    const back = `atlas/maps-art/${mapId}/back.webp`;
    const fore = `atlas/maps-art/${mapId}/fore.webp`;
    return { back: this.has(back) ? back : null, fore: this.has(fore) ? fore : null };
  }

  mapMaskPath(mapId: number): string {
    return `collision/maps/${mapId}/fore.mask`;
  }

  craterMaskPath(ballId: number): string {
    return `collision/craters/${ballId}.mask`;
  }

  hasCrater(ballId: number): boolean {
    return this.has(this.craterMaskPath(ballId));
  }
}

/** Baixa uma imagem como bitmap; `null` se a rede falhar ou o servidor recusar, para o desenho cair no plano B. */
export async function loadBitmap(url: string, fetchImpl: typeof fetch = (...args) => fetch(...args)): Promise<ImageBitmap | null> {
  try {
    const response = await fetchImpl(corsUrl(url));
    if (!response.ok) return null;
    return await createImageBitmap(await response.blob());
  } catch {
    return null;
  }
}
